/**
 * MessageList — 메시지 목록 (자동 스크롤)
 *
 * - 100개 이상 대응: overflow-y-auto + IntersectionObserver 자동 스크롤
 * - 새 메시지 추가 시 하단 자동 스크롤 (스트리밍 중에도)
 * - 사용자가 위로 스크롤 중이면 자동 스크롤 일시 중지
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import type { ChatMessage } from "@modootoday/core-agent/types";
import { MessageBubble } from "./message-bubble";

export interface MessageListProps {
  messages: ChatMessage[];
  isStreaming?: boolean;
  onSend?: (text: string) => void;
  onDeepLink?: (path: string) => void;
  className?: string;
}

export function MessageList({
  messages,
  isStreaming = false,
  onSend,
  onDeepLink,
  className,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // 스크롤 위치 감지 — 하단 근처면 자동 스크롤 활성화
  function handleScroll() {
    const el = containerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAutoScroll(distFromBottom < 100);
  }

  // 새 메시지 / 스트리밍 업데이트 시 자동 스크롤
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, autoScroll]);

  // 처음 마운트 시 즉시 하단으로
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: "end" });
    }
  }, []);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className={cn("flex-1 overflow-y-auto px-4 py-4 space-y-4", className)}
    >
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          onSend={onSend}
          onDeepLink={onDeepLink}
          isStreamingActive={isStreaming}
        />
      ))}
      <div ref={bottomRef} className="h-px" />
    </div>
  );
}
